import React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { formatBRL, formatNumber } from "../utils/formatters";
import BrazilMap from "../BrazilMap";

export default function GeografiaVendasTab({ salesByRegion }) {  // Converter dados para o formato esperado pelo mapa
  // Os dados da API usam orderCount e totalRevenue
  const mapData = salesByRegion ? salesByRegion.map(item => {
    // Tenta identificar qual formato de dados estamos recebendo
    const state = item.state || item.uf || item.stateCode || item.stateId;
    
    return {
      state: state?.toUpperCase() || "",
      // A API usa orderCount, mas mantemos compatibilidade com outros formatos possíveis
      orders: item.orderCount || item.orders || item.totalOrders || 0,
      // A API usa totalRevenue, mas mantemos compatibilidade com outros formatos possíveis
      revenue: item.totalRevenue || item.revenue || 0
    };
  }).filter(item => item.state) : 
  
  // Se não temos dados da API, usamos dados de demonstração
  Object.entries(
    {
      "SP": { orders: 0, revenue: 0 },
    }
  ).map(([state, data]) => ({
    state,
    orders: data.orders,
    revenue: data.revenue
  }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#1a237e' }}>
          Distribuição Geográfica de Vendas
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 3, color: '#607d8b' }}>
          Visualize a distribuição de vendas e receita por estado/região do Brasil.
        </Typography>        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            {/* Mapa do Brasil */}
            <BrazilMap data={mapData} />
            
            {/* Estatísticas resumidas */}
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-around',
              gap: 3,
              mt: 3,
              p: 2,
              backgroundColor: '#f5f7fa',
              borderRadius: 1,
              border: '1px solid #e0e0e0'
            }}>              {/* Total de pedidos */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">Total de Pedidos</Typography>
                <Typography variant="h6" fontWeight={600} color="primary">
                  {formatNumber(mapData.reduce((total, state) => total + (state.orders || 0), 0), 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  em {mapData.length} estados
                </Typography>
              </Box>
              
              {/* Total de receita */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">Receita Total</Typography>
                <Typography variant="h6" fontWeight={600} color="#43a047">
                  {formatBRL(mapData.reduce((total, state) => total + (state.revenue || 0), 0))}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  média de {formatBRL(mapData.reduce((total, state) => total + (state.revenue || 0), 0) / mapData.length)} por estado
                </Typography>
              </Box>
              
              {/* Regiões cobertas */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">Estados Atendidos</Typography>
                <Typography variant="h6" fontWeight={600} color="#1a237e">
                  {mapData.filter(state => state.orders > 0).length} / 27
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            {/* Tabela de dados */}<TableContainer sx={{ 
              maxHeight: 400, 
              border: '1px solid #eee', 
              borderRadius: 2,
              boxShadow: '0px 2px 4px rgba(0,0,0,0.05)'
            }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: '#f0f4f8',
                      color: '#1a237e' 
                    }}>
                      Estado
                    </TableCell>
                    <TableCell align="right" sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: '#f0f4f8',
                      color: '#1a237e' 
                    }}>
                      Pedidos
                    </TableCell>
                    <TableCell align="right" sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: '#f0f4f8',
                      color: '#1a237e' 
                    }}>
                      Receita
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mapData && mapData.length > 0 ? (
                    // Ordenar por quantidade de pedidos (maior para menor)
                    [...mapData]
                      .sort((a, b) => b.orders - a.orders)
                      .map((row, index) => (
                        <TableRow 
                          key={index}
                          sx={{ 
                            '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                            '&:hover': { backgroundColor: '#e8f0fe', cursor: 'pointer' }
                          }}
                        >
                          <TableCell sx={{ fontWeight: index < 3 ? 'bold' : 'normal' }}>
                            {index < 3 && <span style={{ color: '#1a237e', marginRight: '4px' }}>★</span>}
                            {row.state}
                          </TableCell>                          <TableCell align="right" sx={{ 
                            fontWeight: index < 3 ? 'bold' : 'normal',
                            color: index < 3 ? '#1a237e' : 'inherit'
                          }}>
                            {formatNumber(row.orders)}
                            {index < 3 && (
                              <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {((row.orders / mapData.reduce((total, state) => total + (state.orders || 0), 0)) * 100).toFixed(1)}% do total
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ 
                            fontWeight: index < 3 ? 'bold' : 'normal',
                            color: index < 3 ? '#43a047' : 'inherit'
                          }}>
                            {formatBRL(row.revenue)}
                            {index < 3 && (
                              <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {((row.revenue / mapData.reduce((total, state) => total + (state.revenue || 0), 0)) * 100).toFixed(1)}% do total
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        Nenhum dado disponível
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
